import app from 'flarum/forum/app';

/**
 * Fetches and caches the bot's search feeds.
 *
 * The catalogue is ~200KB gzipped and changes only when the RA sync pulls
 * new games, so it is cached in localStorage against the content hash the
 * backend publishes. A member downloads it once and then searches offline
 * until it genuinely changes.
 *
 * Eligibility is the opposite: a few KB, and it goes stale the moment
 * anyone nominates, so it is fetched fresh every time the dialog opens.
 * Caching it would mean showing someone a game that was taken minutes ago.
 */

// Rows are positional to keep the payload small - see build_games_feed in
// cogs/forum_game_feed.py, which is the authority on this order.
export const ID = 0;
export const TITLE = 1;
export const CONSOLE = 2;
export const ACHIEVEMENTS = 3;
export const IS_HACK = 4;
export const HOURS = 5;
const CACHE_KEY = 'jrh-forum-commands.games';
function feedBase() {
  return app.forum.attribute('jrh-forum-commands.feedBase');
}
function versions() {
  return app.forum.attribute('jrh-forum-commands.feedVersions') || {};
}
let memoryCache = null;
export async function loadGames() {
  const version = versions().games;
  if (!version) return null; // bot has never published

  if (memoryCache && memoryCache.version === version) return memoryCache.games;
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
    if (cached && cached.version === version) {
      memoryCache = cached;
      return cached.games;
    }
  } catch (e) {
    // Corrupt or unavailable storage is not a reason to fail - just refetch.
  }
  const response = await fetch("".concat(feedBase(), "/games.json?v=").concat(version));
  if (!response.ok) throw new Error("games feed ".concat(response.status));
  const games = await response.json();
  memoryCache = {
    version,
    games
  };
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(memoryCache));
  } catch (e) {
    // Over quota on a small device: keep working from memory this session.
  }
  return games;
}
export async function loadEligibility(event) {
  if (!versions()["eligibility-".concat(event)]) return {}; // never published

  // Busted on TIME, not on the published version, unlike the catalogue.
  //
  // The versions above come from app.forum.attribute(), which is baked into
  // the page payload when the page loads - so a member who left a tab open
  // this morning would keep requesting the version string from then, and
  // happily serve themselves hours-old eligibility from cache. For a 3-11KB
  // file fetched once per dialog open, always going to the network is the
  // right trade: showing someone a game that was taken an hour ago is the
  // exact failure this feed exists to prevent.
  const response = await fetch("".concat(feedBase(), "/eligibility-").concat(event, ".json?t=").concat(Date.now()), {
    cache: 'no-store'
  });
  if (!response.ok) return {};
  return response.json();
}

/**
 * Mirrors search_games_prefix in utils/ra_api.py: filtered to what the
 * event accepts, titles STARTING with the query ranked above ones merely
 * containing it, and an empty box showing the most achievement-rich games
 * so browsing works without knowing a name.
 */
export function searchGames(games, query, event, limit) {
  if (limit === void 0) {
    limit = 25;
  }
  const hacksOnly = event === 'rhotm';
  const pool = games.filter(g => g[IS_HACK] === hacksOnly);
  const q = (query || '').toLowerCase().trim();
  if (!q) {
    return [...pool].sort((a, b) => b[ACHIEVEMENTS] - a[ACHIEVEMENTS]).slice(0, limit);
  }
  const starts = [];
  const contains = [];
  for (const game of pool) {
    const title = game[TITLE].toLowerCase();
    if (title.startsWith(q)) starts.push(game);else if (title.includes(q)) contains.push(game);
    if (starts.length >= limit) break;
  }
  return starts.concat(contains).slice(0, limit);
}

/**
 * Why this game cannot be nominated right now, or null.
 *
 * A plain lookup by design: every rule behind these reasons - past
 * winners, retired games, disqualifications, already-nominated, alternate
 * editions, and the same-series-as-current-winner check - is resolved into
 * concrete titles by the bot before publishing. Reimplementing any of them
 * here would risk the forum and the validator disagreeing.
 */
export function ineligibleReason(game, eligibility) {
  return eligibility[game[TITLE].toLowerCase()] || null;
}
flarum.reg.add('jrh-forum-commands', 'forum/util/gameFeed', { ID: ID,TITLE: TITLE,CONSOLE: CONSOLE,ACHIEVEMENTS: ACHIEVEMENTS,IS_HACK: IS_HACK,HOURS: HOURS,searchGames: searchGames,ineligibleReason: ineligibleReason, });